/**
 * Theme Shadows
 *
 * Creates MUI-compatible shadow arrays based on the design system.
 * Shadows use alpha values from the palette (duskwraith/echoLight) combined with dropShadow values.
 */

import { PaletteMode, ThemeOptions } from '@mui/material';
import { palette } from './palette';
import { effectsTokens } from './effects-tokens';

/**
 * Creates shadow values for MUI theme
 * MUI expects shadows as an array of strings, where each string is a CSS box-shadow value
 *
 * Shadow sizes map to design system tokens:
 * - xs, sm, base, md, lg, xl, 2xl correspond to different shadow intensities
 */
export const themeShadows = (mode: PaletteMode): ThemeOptions['shadows'] => {
  const { dropShadow } = effectsTokens;
  const { neutrals } = palette;

  // Shadow colors based on mode
  // Base shadows: uses duskwraithA alpha values (works for both light and dark modes)
  const shadowBase = {
    xs: neutrals.duskwraithA[200],
    sm: neutrals.duskwraithA[300],
    base: neutrals.duskwraithA[400],
    md: neutrals.duskwraithA[500],
    lg: neutrals.duskwraithA[600],
    xl: neutrals.duskwraithA[700],
    '2xl': neutrals.duskwraithA[800],
  };

  // Inverse shadows: uses echoLightA alpha values for dark mode, duskwraithA for light mode
  const shadowInverse = {
    xs: mode === 'dark' ? neutrals.echoLightA[200] : neutrals.duskwraithA[200],
    sm: mode === 'dark' ? neutrals.echoLightA[300] : neutrals.duskwraithA[300],
    base: mode === 'dark' ? neutrals.echoLightA[400] : neutrals.duskwraithA[400],
    md: mode === 'dark' ? neutrals.echoLightA[500] : neutrals.duskwraithA[500],
    lg: mode === 'dark' ? neutrals.echoLightA[600] : neutrals.duskwraithA[600],
    xl: mode === 'dark' ? neutrals.echoLightA[700] : neutrals.duskwraithA[700],
    '2xl': mode === 'dark' ? neutrals.echoLightA[800] : neutrals.duskwraithA[800],
  };

  // Helper function to create box-shadow string
  const createShadow = (color: string, blur: string, offsetY?: string, offsetX?: string) => {
    const x = offsetX || '0px';
    const y = offsetY || '0px';
    return `${x} ${y} ${blur} ${color}`;
  };

  // MUI expects 25 shadow values (0-24)
  // We'll create a comprehensive set based on design system tokens
  return [
    'none', // 0 - no shadow
    createShadow(shadowBase.xs, dropShadow[1], '1px'), // 1 - xs
    createShadow(shadowBase.sm, dropShadow[2], '2px'), // 2 - sm
    createShadow(shadowBase.base, dropShadow[4], '4px'), // 3 - base
    createShadow(shadowBase.md, dropShadow[6], '6px'), // 4 - md
    createShadow(shadowBase.lg, dropShadow[8], '8px'), // 5 - lg
    createShadow(shadowBase.xl, dropShadow[10], '10px'), // 6 - xl
    createShadow(shadowBase['2xl'], dropShadow[12], '12px'), // 7 - 2xl
    createShadow(shadowBase.xs, dropShadow[1], '1px', '0px'), // 8 - subtle top shadow
    createShadow(shadowBase.sm, dropShadow[2], '2px', '0px'), // 9
    createShadow(shadowBase.base, dropShadow[4], '4px', '0px'), // 10
    createShadow(shadowBase.md, dropShadow[6], '6px', '0px'), // 11
    createShadow(shadowBase.lg, dropShadow[8], '8px', '0px'), // 12
    createShadow(shadowBase.xl, dropShadow[10], '10px', '0px'), // 13
    createShadow(shadowBase['2xl'], dropShadow[12], '12px', '0px'), // 14
    createShadow(shadowInverse.xs, dropShadow[1], '1px'), // 15 - inverse xs
    createShadow(shadowInverse.sm, dropShadow[2], '2px'), // 16 - inverse sm
    createShadow(shadowInverse.base, dropShadow[4], '4px'), // 17 - inverse base
    createShadow(shadowInverse.md, dropShadow[6], '6px'), // 18 - inverse md
    createShadow(shadowInverse.lg, dropShadow[8], '8px'), // 19 - inverse lg
    createShadow(shadowInverse.xl, dropShadow[10], '10px'), // 20 - inverse xl
    createShadow(shadowInverse['2xl'], dropShadow[12], '12px'), // 21 - inverse 2xl
    createShadow(shadowBase.base, dropShadow[14], '4px'), // 22 - larger blur
    createShadow(shadowBase.lg, dropShadow[16], '6px'), // 23 - larger blur lg
    createShadow(shadowBase.xl, dropShadow[20], '8px'), // 24 - largest blur
  ] as ThemeOptions['shadows'];
};
